import { BingoCard, BingoResponse, ImageData } from '../types';

/**
 * Shuffles an array using the Fisher-Yates algorithm.
 */
function shuffle<T>(array: T[]): T[] {
  console.log("[DEBUG_LOG] bingoService.shuffle - Shuffling array");
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * Generates a range of numbers.
 */
function range(start: number, end: number): number[] {
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

/**
 * Generates a numbers-based bingo card (B:1-15, I:16-30, N:31-45, G:46-60, O:61-75).
 */
function generateNumbersCard(title: string, imageId?: string): BingoCard {
  console.log("[DEBUG_LOG] bingoService.generateNumbersCard - Generating numbers card");
  const b = shuffle(range(1, 15)).slice(0, 5);
  const i = shuffle(range(16, 30)).slice(0, 5);
  const n = shuffle(range(31, 45)).slice(0, 4); // Skip center
  const g = shuffle(range(46, 60)).slice(0, 5);
  const o = shuffle(range(61, 75)).slice(0, 5);

  const header = ["B", "I", "N", "G", "O"];
  const rows: string[][] = [];

  for (let idx = 0; idx < 5; idx++) {
    const row: string[] = [];
    row.push(b[idx].toString());
    row.push(i[idx].toString());
    if (idx === 2) {
      row.push("");
    } else {
      const nIdx = idx < 2 ? idx : idx - 1;
      row.push(n[nIdx].toString());
    }
    row.push(g[idx].toString());
    row.push(o[idx].toString());
    rows.push(row);
  }

  return { title, header, rows, imageId };
}

/**
 * Generates a names-based bingo card with shuffled names and a 'FREE' center.
 */
function generateNamesCard(title: string, names: string[], imageId?: string): BingoCard {
  console.log("[DEBUG_LOG] bingoService.generateNamesCard - Generating names card");
  let shuffledNames = shuffle(names);
  const totalRequired = 24; // 5x5 - center

  if (shuffledNames.length < totalRequired) {
    console.log("[DEBUG_LOG] bingoService.generateNamesCard - Not enough names provided");
    throw new Error(`Nomes insuficientes. São necessários pelo menos ${totalRequired} nomes.`);
  }

  shuffledNames = shuffledNames.slice(0, totalRequired);
  
  const header = ["B", "I", "N", "G", "O"];
  const rows: string[][] = [];

  for (let rIdx = 0; rIdx < 5; rIdx++) {
    const row: string[] = [];
    for (let cIdx = 0; cIdx < 5; cIdx++) {
      if (rIdx === 2 && cIdx === 2) {
        row.push("");
      } else {
        const idx = (rIdx * 5 + cIdx < 12) ? (rIdx * 5 + cIdx) : (rIdx * 5 + cIdx - 1);
        row.push(shuffledNames[idx]);
      }
    }
    rows.push(row);
  }

  return { title, header, rows, imageId };
}

/**
 * Local implementation of bingo card generation.
 */
export const generateBingoCardsLocal = async (request: {
  type: 'numbers' | 'names';
  names: string[];
  title: string;
  count: number;
  image?: string;
}): Promise<BingoResponse> => {
  console.log(`[DEBUG_LOG] bingoService.generateBingoCardsLocal - Generating ${request.count} cards of type ${request.type}`);
  
  // Use setTimeout to ensure the browser has a chance to update the UI (show the spinner)
  // before starting the CPU-intensive work.
  await new Promise(resolve => setTimeout(resolve, 100));

  const imageId = request.image ? crypto.randomUUID() : undefined;
  const images: ImageData[] = imageId && request.image ? [{ id: imageId, data: request.image }] : [];

  const cards: BingoCard[] = [];
  
  // Process in chunks if there are many cards to avoid freezing the UI for too long
  const chunkSize = 50;
  for (let i = 0; i < request.count; i += chunkSize) {
    const currentChunkSize = Math.min(chunkSize, request.count - i);
    for (let j = 0; j < currentChunkSize; j++) {
      if (request.type === 'numbers') {
        cards.push(generateNumbersCard(request.title, imageId));
      } else {
        cards.push(generateNamesCard(request.title, request.names, imageId));
      }
    }
    
    // Give the UI thread a chance to breathe every chunk
    if (i + chunkSize < request.count) {
      await new Promise(resolve => setTimeout(resolve, 0));
    }
  }

  return { cards, images };
};
